import { useCallback, useMemo } from 'react';
import useScript from '../useScript';
import useApi from '../useApi';
import { usePlaidCreateItemMutation } from './api/usePlaidCreateItemMutation';
import { usePlaidUpdateItemMutation } from './api/usePlaidUpdateItemMutation';

enum VerificationStatusDTO {
  PendingAutomaticVerification = 'pending_automatic_verification',
  PendingManualVerification = 'pending_manual_verification',
  AutomaticallyVerified = 'automatically_verified',
  ManuallyVerified = 'manually_verified',
  VerificationExpired = 'verification_expired',
  VerificationFailed = 'verification_failed',
}

export interface AccountDTO {
  id: string;
  name: string;
  mask: string;
  type: string;
  subtype: string;
  verification_status: VerificationStatusDTO;
}

export enum PlaidLinkType {
  Create = 'Create',
  Update = 'Update',
}

interface InstitutionDTO {
  name: string;
  institution_id: string;
}

interface MetadataDTO {
  institution: InstitutionDTO;
  accounts: AccountDTO[];
  link_session_id: string;
}

interface ResponseDTO {
  itemId: string;
}

const PLAID_LINK_STABLE_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

declare const Plaid: { create: Function };

export const usePlaidLink = ({
  clientId,
  isDataReconciliation = false,
  isAccountVerification = false,
  onSuccess = () => {},
  onExit = () => {},
  onStart = () => {},
  onHandOff = () => {},
  onError = () => {},
}: {
  clientId: string | number;
  isDataReconciliation?: boolean;
  isAccountVerification?: boolean;
  onSuccess?: (metadata: MetadataDTO, response: ResponseDTO, linkType: PlaidLinkType) => void;
  onStart?: () => void;
  onExit?: () => void;
  onHandOff?: (metadata: any) => void;
  onError?: (error: Error) => void;
}) => {
  const [isScriptLoaded] = useScript(PLAID_LINK_STABLE_URL);

  const [fetchToken, { loading: isLoadingToken, errors: fetchTokenErrors }] = useApi(
    '/Consumer/PlaidAccountsLinking/GetLinkToken',
  );

  const [
    fetchUpdateToken,
    { loading: isLoadingUpdateToken, errors: fetchUpdateTokenErrors },
  ] = useApi('/Consumer/PlaidAccountsLinking/GetUpdateLinkToken');

  const {
    mutateAsync: createItem,
    isLoading: createItemIsLoading,
    error: createItemError,
  } = usePlaidCreateItemMutation();

  const {
    mutateAsync: updateItem,
    isLoading: updateItemIsLoading,
    error: updateItemError,
  } = usePlaidUpdateItemMutation();

  const openPlaid = useCallback(
    (token: string, itemId?: string) => {
      if (isScriptLoaded) {
        onStart();

        const handler = Plaid.create({
          token,
          onSuccess: async (publicToken, metadata) => {
            if (itemId) {
              try {
                const itemResponse = await updateItem({
                  clientId,
                  itemId,
                  isDataReconciliation,
                });

                onSuccess(metadata, itemResponse, PlaidLinkType.Update);
              } catch (err) {
                if (err instanceof Error) {
                  onError(err);
                }
              }
            } else {
              try {
                const response = await createItem({
                  clientId,
                  publicToken,
                  isDataReconciliation,
                  isAccountVerification,
                });
                onSuccess(metadata, response, PlaidLinkType.Create);
              } catch (err) {
                if (err instanceof Error) {
                  onError(err);
                }
              }
            }
          },
          onExit: () => {
            handler.destroy();
            onExit();
          },
          onEvent: (eventName, metadata) => {
            if (eventName === 'HANDOFF') {
              onHandOff(metadata);
            }
          },
          receivedRedirectUri: null,
        });

        handler.open();
      }
    },
    [
      clientId,
      createItem,
      isAccountVerification,
      isDataReconciliation,
      isScriptLoaded,
      onError,
      onExit,
      onHandOff,
      onStart,
      onSuccess,
      updateItem,
    ],
  );

  const open = useCallback(
    (itemId?: string) => {
      const isDataReconciliationParam = isDataReconciliation ? 'true' : 'false';

      if (itemId) {
        fetchUpdateToken({
          queryParams: { clientId, itemId, isDataReconciliation: isDataReconciliationParam },
          onSuccess: ({ linkToken }) => openPlaid(linkToken, itemId),
        });
      } else {
        fetchToken({
          queryParams: {
            clientId,
            isDataReconciliation: isDataReconciliationParam,
            isAccountVerification: isAccountVerification.toString(),
          },
          onSuccess: ({ linkToken }) => openPlaid(linkToken),
        });
      }
    },
    [
      clientId,
      fetchToken,
      fetchUpdateToken,
      isDataReconciliation,
      openPlaid,
      isAccountVerification,
    ],
  );

  const queryError = useMemo(() => {
    if (fetchTokenErrors.length > 0) {
      return new Error(fetchTokenErrors[0]);
    }

    if (fetchUpdateTokenErrors.length > 0) {
      return new Error(fetchUpdateTokenErrors[0]);
    }

    return null;
  }, [fetchTokenErrors, fetchUpdateTokenErrors]);

  return {
    open,
    isLoading: isLoadingToken || isLoadingUpdateToken || createItemIsLoading || updateItemIsLoading,
    queryError,
    mutationError: createItemError || updateItemError || null,
    isScriptLoaded,
  };
};
